import type { MeMenuItem } from '../../api/services/me.service'

/** Una opción navegable del menú, con su sección y grupo ya resueltos. */
export interface NavLeaf {
  item: MeMenuItem
  section: MeMenuItem
  group: MeMenuItem | null
}

export function flattenLeaves(menu: MeMenuItem[]): NavLeaf[] {
  const out: NavLeaf[] = []
  for (const section of menu) {
    if (section.route && !(section.children?.length)) {
      out.push({ item: section, section, group: null })
      continue
    }
    for (const child of section.children ?? []) {
      if (child.isGroup) {
        for (const leaf of child.children ?? []) {
          if (!leaf.isGroup) out.push({ item: leaf, section, group: child })
        }
      } else {
        out.push({ item: child, section, group: null })
      }
    }
  }
  return out
}

/** La opción activa para una ruta: la de prefijo más largo (así /x/123 activa /x). */
export function findActive(leaves: NavLeaf[], pathname: string): NavLeaf | null {
  let best: NavLeaf | null = null
  for (const l of leaves) {
    const r = l.item.route
    if (!r) continue
    if (pathname === r || pathname.startsWith(r + '/')) {
      if (!best || r.length > (best.item.route?.length ?? 0)) best = l
    }
  }
  return best
}

/** Rutas que piden consentimiento antes de entrar (registro de personas/transportistas). */
export const CONSENT_PATHS = ['/app/personas/nuevo', '/app/transportistas/nuevo']

export const canOpen = (item: MeMenuItem) => item.allowed && !item.isComingSoon && !!item.route

/** Normaliza para buscar sin tildes ni mayúsculas. */
export const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/** Puntaje simple de coincidencia difusa: subsecuencia, con premio a inicio de palabra. */
export function fuzzyScore(query: string, text: string): number {
  const q = norm(query.trim())
  if (!q) return 1
  const t = norm(text)
  const idx = t.indexOf(q)
  if (idx >= 0) return 100 - idx + (idx === 0 || t[idx - 1] === ' ' ? 40 : 0)
  let ti = 0
  let score = 0
  for (const ch of q) {
    const found = t.indexOf(ch, ti)
    if (found < 0) return 0
    score += found === ti ? 3 : 1
    ti = found + 1
  }
  return score
}

const RECENTS_KEY = 'kuntur.nav.recents'

export function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY)
    return raw ? (JSON.parse(raw) as string[]) : []
  } catch {
    return []
  }
}

export function pushRecent(route: string) {
  try {
    const next = [route, ...readRecents().filter(r => r !== route)].slice(0, 6)
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next))
  } catch {
    /* sin persistencia */
  }
}
